/**
 * AN INVOICE AND A RECEIPT CARRY A NUMBER (ops #76 O2, the owner's D3 of 2026-10-17). An invoice is
 * `FK-` and a plain count from 1, given when it is first sent and never reused, so a draft has none and
 * the sent invoices have no gaps; a receipt is `FK-R` and a count of its own, given when a payment is
 * recorded. A count from 1 is honest about being new. The counts live in `counters`, one row a word,
 * since migration `0033`; these are only the words they are written in, which every reader shares.
 *
 * AND THE ZELLE MEMO BECOMES THE NUMBER (ops #71, item 1): `FK-42` once the invoice has one, the day
 * of the clean, `FK Oct 27`, for one sent before numbering began, so two invoices of one day no longer
 * share a memo. One function, `invoiceMemo`, read by her link's page, the push to home and the office's
 * screen, so the three never disagree.
 */
import { zelleMemo } from './zelle.ts';

/** The two counts `counters` keeps, one row each: `ck_counters_key` is built from this list. */
export const COUNTER_KEYS = ['invoice', 'receipt'] as const;
export type CounterKey = (typeof COUNTER_KEYS)[number];

export const INVOICE_NUMBER_PREFIX = 'FK-';
export const RECEIPT_NUMBER_PREFIX = 'FK-R';

function counted(prefix: string, count: number): string {
  if (!Number.isInteger(count) || count < 1) throw new Error(`not a count: ${count}`);
  return `${prefix}${count}`;
}

/** The invoice numbered `count`, `FK-42`. A count is a whole number from 1; anything else throws. */
export function invoiceNumber(count: number): string {
  return counted(INVOICE_NUMBER_PREFIX, count);
}

/** The receipt numbered `count`, `FK-R7`. A count is a whole number from 1; anything else throws. */
export function receiptNumber(count: number): string {
  return counted(RECEIPT_NUMBER_PREFIX, count);
}

/**
 * THE MEMO A ZELLE PAYMENT OF THIS INVOICE CARRIES: its number once it has one, the day of the clean
 * (`zelleMemo`) for one sent before numbering began, and nothing with neither. `day` is the clean's,
 * or the issue date where the clean's is not known, as each reader has always passed it.
 */
export function invoiceMemo(invoice: {
  readonly number: string | null;
  readonly day: string;
}): string;
export function invoiceMemo(invoice: {
  readonly number: string | null;
  readonly day: string | null;
}): string | null;
export function invoiceMemo(invoice: {
  readonly number: string | null;
  readonly day: string | null;
}): string | null {
  return invoice.number ?? (invoice.day ? zelleMemo(invoice.day) : null);
}
